import React, { useState } from "react";
import { Formik, Form, Field, FieldArray } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { apiPost, apiPut } from "../../utils/api";
import { RichEditor } from "../subcomponents/RichEditor";
import { ImageUpload } from "../subcomponents/ImageUpload";
import { useLanguage } from "../../contexts/LanguageContext";
import { AdminFormDialog, AdminField } from "./AdminFormDialog";
import { Input, SelectNative } from "../ui/input";
import { Button } from "../ui/button";

type FiliereForm = {
  id?: number;
  name: string;
  nameEn: string;
  description: string;
  descriptionEn: string;
  duration: string;
  durationEn: string;
  diploma: string;
  diplomaEn: string;
};

export type FacultyFormState = {
  name: string;
  nameEn: string;
  domaine: string;
  domaineEn: string;
  description: string;
  descriptionEn: string;
  dean: string;
  deanEn: string;
  secretary: string;
  secretaryEn: string;
  deanStaffId: string;
  secretaryStaffId: string;
  coverImage: string;
  filieres: FiliereForm[];
};

export const EMPTY_FILIERE: FiliereForm = {
  name: "", nameEn: "", description: "", descriptionEn: "",
  duration: "", durationEn: "", diploma: "", diplomaEn: "",
};

export const EMPTY_FACULTY: FacultyFormState = {
  name: "", nameEn: "", domaine: "", domaineEn: "",
  description: "", descriptionEn: "",
  dean: "", deanEn: "", secretary: "", secretaryEn: "",
  deanStaffId: "", secretaryStaffId: "",
  coverImage: "", filieres: [],
};

const buildInitial = (editing: any | null): FacultyFormState =>
  editing
    ? {
        name: editing.name || "",
        nameEn: editing.nameEn || "",
        domaine: editing.domaine || "",
        domaineEn: editing.domaineEn || "",
        description: editing.description || "",
        descriptionEn: editing.descriptionEn || "",
        dean: editing.dean || "",
        deanEn: editing.deanEn || "",
        secretary: editing.secretary || "",
        secretaryEn: editing.secretaryEn || "",
        deanStaffId: editing.deanStaffId ? String(editing.deanStaffId) : "",
        secretaryStaffId: editing.secretaryStaffId ? String(editing.secretaryStaffId) : "",
        coverImage: editing.coverImage || "",
        filieres: (editing.filieres || []).map((f: any) => ({
          id: f.id,
          name: f.name || "",
          nameEn: f.nameEn || "",
          description: f.description || "",
          descriptionEn: f.descriptionEn || "",
          duration: f.duration || "",
          durationEn: f.durationEn || "",
          diploma: f.diploma || "",
          diplomaEn: f.diplomaEn || "",
        })),
      }
    : EMPTY_FACULTY;

type Props = {
  open: boolean;
  editing: any | null;
  staffMembers?: any[];
  onClose: () => void;
  onSaved: () => void;
};

const FORM_ID = "faculty-form";

const StaffSelect: React.FC<{
  name: "deanStaffId" | "secretaryStaffId";
  textField: "dean" | "secretary";
  staffMembers: any[];
  placeholder: string;
  testId: string;
}> = ({ name, textField, staffMembers, placeholder, testId }) => {
  if (staffMembers.length === 0) {
    return <Field as={Input} name={textField} data-testid={testId} />;
  }
  return (
    <Field name={name}>
      {({ field, form }: any) => (
        <SelectNative
          {...field}
          data-testid={testId}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
            form.setFieldValue(name, e.target.value);
            const member = staffMembers.find((s) => String(s.id) === e.target.value);
            if (member) form.setFieldValue(textField, member.name);
          }}
        >
          <option value="">{placeholder}</option>
          {staffMembers.map((s: any) => (
            <option key={s.id} value={s.id}>{s.name} — {s.role}</option>
          ))}
        </SelectNative>
      )}
    </Field>
  );
};

export const AdminFacultyModal: React.FC<Props> = ({ open, editing, staffMembers = [], onClose, onSaved }) => {
  const { t } = useLanguage();
  const [localeTab, setLocaleTab] = useState<"fr" | "en">("fr");

  const schema = Yup.object({
    name: Yup.string().trim().required(t.common.required),
    domaine: Yup.string().trim().required(t.common.required),
    nameEn: Yup.string(),
    domaineEn: Yup.string(),
    description: Yup.string(),
    descriptionEn: Yup.string(),
    dean: Yup.string(),
    deanEn: Yup.string(),
    secretary: Yup.string(),
    secretaryEn: Yup.string(),
    deanStaffId: Yup.string(),
    secretaryStaffId: Yup.string(),
    coverImage: Yup.string(),
    filieres: Yup.array().of(
      Yup.object({
        name: Yup.string(),
        nameEn: Yup.string(),
        description: Yup.string(),
        descriptionEn: Yup.string(),
        duration: Yup.string(),
        durationEn: Yup.string(),
        diploma: Yup.string(),
        diplomaEn: Yup.string(),
      })
    ),
  });

  const isFr = localeTab === "fr";
  const selectPlaceholder = t.admin.selectStaffMember || "— Sélectionner —";

  return (
    <Formik
      initialValues={buildInitial(editing)}
      validationSchema={schema}
      enableReinitialize
      onSubmit={async (values, { setSubmitting }) => {
        const payload = {
          ...values,
          deanStaffId: values.deanStaffId ? Number(values.deanStaffId) : null,
          secretaryStaffId: values.secretaryStaffId ? Number(values.secretaryStaffId) : null,
          filieres: values.filieres.filter((f) => f.name.trim()),
        };
        try {
          if (editing) {
            await apiPut(`/faculties/${editing.id}`, payload);
          } else {
            await apiPost("/faculties", payload);
          }
          toast.success(t.common.saveSuccess);
          onSaved();
          onClose();
        } catch (e: any) {
          toast.error(e?.response?.data?.reason || e?.messageText || t.common.error);
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ values, errors, touched, isSubmitting, setFieldValue }) => (
        <AdminFormDialog
          open={open}
          onClose={onClose}
          title={editing ? t.admin.editFaculty : t.admin.newFaculty}
          subtitle={t.admin.pageContent}
          testId="faculty-modal"
          className="max-w-3xl"
          formId={FORM_ID}
          submitLabel={editing ? t.admin.save : t.admin.create}
          isSubmitting={isSubmitting}
          showLocaleTabs
          localeTab={localeTab}
          onLocaleChange={setLocaleTab}
        >
          <Form id={FORM_ID} className="grid gap-4 pb-4">
            {isFr ? (
              <>
                <AdminField label={`${t.admin.name} (FR)`} required error={touched.name ? errors.name : undefined}>
                  <Field as={Input} name="name" data-testid="faculty-name" />
                </AdminField>
                <AdminField label={`${t.admin.domains} (FR)`} required error={touched.domaine ? errors.domaine : undefined}>
                  <Field as={Input} name="domaine" data-testid="faculty-domaine" />
                </AdminField>
                <AdminField label={`${t.admin.pageContent} (FR)`}>
                  <RichEditor
                    value={values.description}
                    onChange={(html) => setFieldValue("description", html)}
                    folder="ulpgl/faculties"
                    placeholder={t.admin.facultyContentPlaceholder}
                    minHeight={320}
                  />
                </AdminField>
                <div className="grid grid-cols-2 gap-4">
                  <AdminField label={`${t.admin.dean} (FR)`}>
                    <StaffSelect
                      name="deanStaffId"
                      textField="dean"
                      staffMembers={staffMembers}
                      placeholder={selectPlaceholder}
                      testId="faculty-dean"
                    />
                    {!values.deanStaffId && (
                      <Field as={Input} name="dean" className="mt-2" placeholder={t.admin.deanManual || "Nom du doyen (manuel)"} />
                    )}
                  </AdminField>
                  <AdminField label={`${t.admin.secretary} (FR)`}>
                    <StaffSelect
                      name="secretaryStaffId"
                      textField="secretary"
                      staffMembers={staffMembers}
                      placeholder={selectPlaceholder}
                      testId="faculty-secretary"
                    />
                    {!values.secretaryStaffId && (
                      <Field as={Input} name="secretary" className="mt-2" placeholder={t.admin.secretaryManual || "Nom du secrétariat (manuel)"} />
                    )}
                  </AdminField>
                </div>
              </>
            ) : (
              <>
                <AdminField label={`${t.admin.name} (EN)`}>
                  <Field as={Input} name="nameEn" />
                </AdminField>
                <AdminField label={`${t.admin.domains} (EN)`}>
                  <Field as={Input} name="domaineEn" />
                </AdminField>
                <AdminField label={`${t.admin.pageContent} (EN)`}>
                  <RichEditor
                    value={values.descriptionEn}
                    onChange={(html) => setFieldValue("descriptionEn", html)}
                    folder="ulpgl/faculties"
                    placeholder={t.admin.facultyContentPlaceholderEn}
                    minHeight={320}
                  />
                </AdminField>
                <div className="grid grid-cols-2 gap-4">
                  <AdminField label={`${t.admin.dean} (EN)`}>
                    <Field as={Input} name="deanEn" data-testid="faculty-dean-en" />
                  </AdminField>
                  <AdminField label={`${t.admin.secretary} (EN)`}>
                    <Field as={Input} name="secretaryEn" data-testid="faculty-secretary-en" />
                  </AdminField>
                </div>
              </>
            )}

            <ImageUpload
              value={values.coverImage}
              onChange={(url) => setFieldValue("coverImage", url)}
              folder="ulpgl/faculties"
              label={t.admin.coverImage}
              testId="faculty-cover"
            />

            <div className="border-t pt-4 mt-2">
              <div className="flex justify-between items-center mb-3">
                <h4 className="text-sm font-semibold text-foreground m-0">{t.admin.programs}</h4>
                <FieldArray name="filieres">
                  {({ push }) => (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => push({ ...EMPTY_FILIERE })}
                    >
                      + {t.admin.addProgram}
                    </Button>
                  )}
                </FieldArray>
              </div>
              <FieldArray name="filieres">
                {({ remove }) =>
                  values.filieres.map((fil, idx) => (
                    <div key={fil.id || `new-${idx}`} className="filiere-block">
                      <div className="flex justify-between mb-2">
                        <span className="text-xs font-bold text-muted-foreground">#{idx + 1}</span>
                        <button
                          type="button"
                          onClick={() => remove(idx)}
                          className="text-xs text-destructive bg-transparent border-0 cursor-pointer"
                        >
                          {t.admin.delete}
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Field as={Input} name={`filieres.${idx}.name`} placeholder="Nom FR" />
                        <Field as={Input} name={`filieres.${idx}.nameEn`} placeholder="Name EN" />
                      </div>
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        <Field as={Input} name={`filieres.${idx}.duration`} placeholder="Durée FR" />
                        <Field as={Input} name={`filieres.${idx}.durationEn`} placeholder="Duration EN" />
                      </div>
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        <Field as={Input} name={`filieres.${idx}.diploma`} placeholder="Diplôme FR" />
                        <Field as={Input} name={`filieres.${idx}.diplomaEn`} placeholder="Degree EN" />
                      </div>
                    </div>
                  ))
                }
              </FieldArray>
            </div>
          </Form>
        </AdminFormDialog>
      )}
    </Formik>
  );
};
